import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ICreateBucketAutoDebitConfiguration } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig, getSavingsBusinessId } from '../utils';

interface ICreateBucketRequest {
  requestReference?: string;
  userId?: string;
  customerId?: string;
  businessId?: string;
  bucketName: string;
  accountName: string;
  productId: string;
  liquidationAccountNumber: string;
  commencementDate?: string;
  initialDepositAmount: number;
  FixedSavingsInterestPaymentFrequency: string;
  RollOver: boolean;
  goalAmount?: string;
  tenureInDays?: number;
  userChannel: string;
  activateSpendAndStash?: boolean;
  spendAndStashPercentage?: string;
  logoUrl: string;
  activateAutoDebit: boolean;
  autoDebitConfiguration?: ICreateBucketAutoDebitConfiguration | null;
  targetDate?: string;
  pin?: string;
}

interface ICreateBucketResponse {
  status?: boolean;
  message?: string;
  data?: {
    requestReference: string;
    id: string;
    accountNumber: string;
  };

  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreateBucketResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createBucketAsync = createAsyncThunk(
  'bucket/create',
  async (values: ICreateBucketRequest, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const authUser = state.auth.value?.data?.user;
      const businessId = getSavingsBusinessId(state);
      const selectedBusiness =
        authUser?.businesses?.find((b) => b.id === businessId) ??
        authUser?.businesses?.[0];

      const body: ICreateBucketRequest = {
        ...values,
        requestReference: values.requestReference || crypto.randomUUID(),
        userId: values.userId || businessId || '',
        customerId: selectedBusiness?.customerId ?? '',
        businessId: values.businessId || businessId || '',
      };

      const { data } = await api.post<ICreateBucketResponse>(
        '/savings/accounts',
        body,
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreateBucketResponse>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const bucketCreationSlice = createSlice({
  name: 'bucket/create',
  initialState,
  reducers: {
    resetBucketCreation(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createBucketAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createBucketAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreateBucketResponse;
      })
      .addCase(createBucketAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreateBucketResponse;
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetBucketCreation } = bucketCreationSlice.actions;

export default bucketCreationSlice.reducer;
